/* Agendamientos - detalle de una agenda para gestores y responsables (TD-11766).
 *
 * - Gestores (creador, co-gestores y admins): editan, pausan/finalizan y
 *   atienden todas las citas.
 * - Responsables internos: ven y atienden solo sus citas (aprobar, cancelar,
 *   marcar asistencia).
 * En agendas confidenciales las respuestas del formulario solo las ve el
 * responsable de cada cita (lo resuelve el backend).
 */
import { IonBadge, IonButton, IonContent, IonIcon, IonPage, useIonAlert, useIonToast } from "@ionic/react";
import { CircularProgress, TextField } from "@material-ui/core";
import {
  arrowBack,
  calendarOutline,
  checkmarkOutline,
  closeOutline,
  createOutline,
  downloadOutline,
  locationOutline,
  lockClosedOutline,
  logoGoogle,
  pauseOutline,
  playOutline,
  stopOutline,
  timeOutline,
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Header from "src/components/Header";
import AgendaAPI, { Asistencia, EstadoReserva, ReservaGestion, TemaDetalle, Turno } from "src/services/agenda";
import {
  agruparPorDia,
  CATEGORIAS,
  colorCategoria,
  ESTADO_RESERVA,
  ESTADO_TEMA,
  ETIQUETA_MODALIDAD,
  ETIQUETA_PUBLICO,
  eventoDeReservaGestion,
  eventosDeGestion,
  formatDia,
  hora,
  resumenHorario,
  ymdLocal,
} from "src/utils/agenda";
import { mensajeError } from "src/utils/errores";
import { descargarIcs, urlGoogleCalendar } from "src/utils/calendario";
import { exportarExcel } from "src/utils/exportarExcel";
import "./Agenda.css";

type Vista = "RESERVAS" | "TURNOS";

/** 'YYYY-MM-DD' -> 'dd/mm/aaaa' */
const formatFecha = (ymd: string) => ymd.split("-").reverse().join("/");
type FiltroEstado = "ACTIVAS" | EstadoReserva | "TODAS";

const FILTROS: Array<{ valor: FiltroEstado; etiqueta: string }> = [
  { valor: "ACTIVAS", etiqueta: "Activas" },
  { valor: "PENDIENTE", etiqueta: "Por aprobar" },
  { valor: "ESPERA", etiqueta: "En espera" },
  { valor: "CANCELADA", etiqueta: "Canceladas" },
  { valor: "RECHAZADA", etiqueta: "Rechazadas" },
  { valor: "TODAS", etiqueta: "Todas" },
];

const AgendaTemaDetalle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const temaId = Number(id);
  const history = useHistory();
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();

  const [tema, setTema] = useState<TemaDetalle | null>(null);
  const [cargando, setCargando] = useState(true);
  const [vista, setVista] = useState<Vista>("RESERVAS");

  const [desde, setDesde] = useState(ymdLocal(-7));
  const [hasta, setHasta] = useState(ymdLocal(30));
  const [filtro, setFiltro] = useState<FiltroEstado>("ACTIVAS");
  const [reservas, setReservas] = useState<ReservaGestion[]>([]);
  const [cargandoReservas, setCargandoReservas] = useState(false);
  const [trabajando, setTrabajando] = useState<number | null>(null);

  const [turnosDesde, setTurnosDesde] = useState(ymdLocal());
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [cargandoTurnos, setCargandoTurnos] = useState(false);

  const cargarTema = useCallback(async () => {
    try {
      setTema(await AgendaAPI.obtenerGestion(temaId));
    } catch (err: any) {
      present({ message: mensajeError(err, "No se pudo cargar la agenda"), duration: 3500, color: "danger" });
      history.replace("/agenda/gestion");
    } finally {
      setCargando(false);
    }
  }, [temaId, history, present]);

  const cargarReservas = useCallback(async () => {
    setCargandoReservas(true);
    try {
      const res = await AgendaAPI.reservasGestion(temaId, { desde, hasta });
      setReservas(res.datos);
    } catch (err: any) {
      present({ message: mensajeError(err, "No se pudieron cargar las reservas"), duration: 3500, color: "danger" });
    } finally {
      setCargandoReservas(false);
    }
  }, [temaId, desde, hasta, present]);

  useEffect(() => {
    cargarTema();
  }, [cargarTema]);

  const temaCargado = Boolean(tema);

  useEffect(() => {
    if (tema) cargarReservas();
    // Solo al tener el tema o cambiar el rango.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tema?.id, desde, hasta]);

  useEffect(() => {
    if (!temaCargado || vista !== "TURNOS") return;
    setCargandoTurnos(true);
    const fin = new Date(`${turnosDesde}T00:00:00`);
    fin.setDate(fin.getDate() + 13);
    const finYmd = `${fin.getFullYear()}-${String(fin.getMonth() + 1).padStart(2, "0")}-${String(fin.getDate()).padStart(2, "0")}`;
    AgendaAPI.turnosGestion(temaId, turnosDesde, finYmd)
      .then(setTurnos)
      .catch((err) => {
        setTurnos([]);
        present({ message: mensajeError(err, "No se pudo cargar la ocupación de turnos"), duration: 3500, color: "danger" });
      })
      .finally(() => setCargandoTurnos(false));
  }, [temaCargado, vista, turnosDesde, temaId, present]);

  const visibles = useMemo(
    () =>
      reservas.filter((r) =>
        filtro === "TODAS"
          ? true
          : filtro === "ACTIVAS"
          ? r.estado === "CONFIRMADA" || r.estado === "PENDIENTE" || r.estado === "ESPERA"
          : r.estado === filtro
      ),
    [reservas, filtro]
  );

  /* ---------------- Acciones sobre la agenda ---------------- */
  const cambiarEstado = (estado: "PUBLICADO" | "PAUSADO" | "FINALIZADO") => {
    const textos = {
      PUBLICADO: ["Reactivar agenda", "La agenda vuelve a aceptar reservas."],
      PAUSADO: ["Pausar agenda", "No se aceptan reservas nuevas; las citas tomadas siguen vigentes."],
      FINALIZADO: ["Finalizar agenda", "Se cancelan todas las citas futuras y se avisa a cada persona. No se puede deshacer."],
    }[estado];
    presentAlert({
      header: textos[0],
      message: textos[1],
      buttons: [
        "Volver",
        {
          text: "Confirmar",
          role: estado === "FINALIZADO" ? "destructive" : undefined,
          handler: async () => {
            try {
              const res = await AgendaAPI.cambiarEstado(temaId, estado);
              present({ message: res.info, duration: 3000, color: "success" });
              await Promise.all([cargarTema(), cargarReservas()]);
            } catch (err: any) {
              present({ message: mensajeError(err, "No se pudo cambiar el estado"), duration: 3500, color: "danger" });
            }
          },
        },
      ],
    });
  };

  /* ---------------- Acciones sobre reservas ---------------- */
  const ejecutar = async (reservaId: number, accion: () => Promise<{ info: string }>) => {
    setTrabajando(reservaId);
    try {
      const res = await accion();
      present({ message: res.info, duration: 2500, color: "success" });
      await Promise.all([cargarReservas(), cargarTema()]);
    } catch (err: any) {
      present({ message: mensajeError(err, "No se pudo completar la acción"), duration: 3500, color: "danger" });
    } finally {
      setTrabajando(null);
    }
  };

  const pedirMotivo = (titulo: string, boton: string, accion: (motivo: string) => void) =>
    presentAlert({
      header: titulo,
      message: "La persona recibe el motivo en un aviso.",
      inputs: [{ name: "motivo", type: "textarea", placeholder: "Motivo" }],
      buttons: [
        "Volver",
        {
          text: boton,
          role: "destructive",
          handler: (data: { motivo?: string }) => {
            const motivo = (data?.motivo || "").trim();
            if (!motivo) {
              present({ message: "Indicá el motivo", duration: 2500, color: "warning" });
              return false;
            }
            accion(motivo);
            return true;
          },
        },
      ],
    });

  const marcarAsistencia = (r: ReservaGestion, valor: Asistencia) =>
    ejecutar(r.id, () => AgendaAPI.asistencia(r.id, r.asistencia === valor ? null : valor));

  const exportar = async () => {
    if (!tema || !reservas.length) return;
    const filas = reservas.map((r) => ({
      fecha: r.fecha_inicio.slice(0, 10),
      hora: hora(r.fecha_inicio),
      responsable: r.responsable_nombre || "",
      cedula: r.cedula,
      nombre: r.nombre || "",
      cargo: r.cargo || "",
      departamento: r.departamento || "",
      estado: ESTADO_RESERVA[r.estado].etiqueta,
      asistencia: r.asistencia || "",
      motivo: r.motivo || "",
      encuesta: r.encuesta_puntaje ?? "",
      comentario: r.encuesta_comentario || "",
      respuestas: (r.respuestas || []).map((a) => `${a.pregunta}: ${a.valor}`).join(" | "),
    }));
    const columnas = [
      { header: "Fecha", key: "fecha" },
      { header: "Hora", key: "hora" },
      { header: "Responsable", key: "responsable" },
      { header: "Cédula", key: "cedula" },
      { header: "Nombre", key: "nombre" },
      { header: "Cargo", key: "cargo" },
      { header: "Departamento", key: "departamento" },
      { header: "Estado", key: "estado" },
      { header: "Asistencia", key: "asistencia" },
      { header: "Motivo", key: "motivo" },
      { header: "Encuesta (1-5)", key: "encuesta" },
      { header: "Comentario", key: "comentario" },
    ];
    // En agendas confidenciales las respuestas no salen del sistema.
    if (!tema.confidencial) columnas.push({ header: "Respuestas", key: "respuestas" });
    try {
      await exportarExcel(filas, columnas, `Agenda_${tema.titulo}_${desde}_a_${hasta}`, "Reservas");
    } catch {
      present({ message: "No se pudo generar el Excel", duration: 3000, color: "danger" });
    }
  };

  /** Todas las citas confirmadas y pendientes del rango, en un .ics para importar. */
  const descargarCalendario = async () => {
    if (!tema) return;
    const eventos = eventosDeGestion(tema, reservas);
    if (!eventos.length) {
      present({ message: "No hay citas confirmadas ni pendientes en este rango", duration: 3000, color: "warning" });
      return;
    }
    try {
      await descargarIcs(eventos, `Agenda ${tema.titulo} ${desde} a ${hasta}`);
    } catch {
      present({ message: "No se pudo generar el calendario", duration: 3000, color: "danger" });
    }
  };

  if (cargando || !tema) {
    return (
      <IonPage>
        <Header titulo="Agenda" botonIzq={{ icono: arrowBack, url: "/agenda/gestion" }} />
        <IonContent>
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const st = tema.estadisticas || ({} as TemaDetalle["estadisticas"]);
  const color = colorCategoria(tema.categoria);
  const gestiona = tema.rol.puede_gestionar;
  const nombreResp = (rid: number | null) => tema.responsables.find((r) => r.id === rid)?.nombre;

  return (
    <IonPage>
      <Header titulo="Agenda" botonIzq={{ icono: arrowBack, url: "/agenda/gestion" }} />
      <IonContent id="agenda-tema-detalle">
        <div className="agenda-page campos-mui">
          {/* ---------------- Encabezado ---------------- */}
          <div className="agenda-card acento" style={{ ["--agenda-color" as any]: color }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div>
                <span className="agenda-etiqueta">{CATEGORIAS[tema.categoria]?.etiqueta}</span>
                <p className="agenda-card-titulo" style={{ fontSize: 18 }}>{tema.titulo}</p>
              </div>
              <IonBadge color={ESTADO_TEMA[tema.estado].color} style={{ alignSelf: "flex-start" }}>
                {ESTADO_TEMA[tema.estado].etiqueta}
              </IonBadge>
            </div>
            {tema.descripcion && <p className="agenda-card-sub" style={{ whiteSpace: "pre-line" }}>{tema.descripcion}</p>}
            <div className="agenda-meta">
              <span>{tema.tipo_agenda === "INDIVIDUAL" ? "Turnos de" : "Sesiones de"} {tema.duracion_minutos} min</span>
              {tema.tipo_agenda === "GRUPAL" && <span>Cupo {tema.cupo_por_turno}</span>}
              <span>{ETIQUETA_MODALIDAD[tema.modalidad]}</span>
              {tema.lugar && (
                <span>
                  <IonIcon icon={locationOutline} /> {tema.lugar}
                </span>
              )}
              <span>{ETIQUETA_PUBLICO[tema.publico_tipo]}</span>
              {tema.requiere_aprobacion && <span>Con aprobación</span>}
              {tema.confidencial && (
                <span>
                  <IonIcon icon={lockClosedOutline} /> Confidencial
                </span>
              )}
            </div>
            {tema.responsables.length > 0 && (
              <div className="agenda-meta">
                <span>Atiende(n): {tema.responsables.map((r) => r.nombre).join(", ")}</span>
              </div>
            )}
            <div className="agenda-meta">
              <span>
                <IonIcon icon={calendarOutline} />{" "}
                {!tema.vigencia_desde
                  ? "Fechas sin definir"
                  : tema.vigencia_hasta
                  ? `Del ${formatFecha(tema.vigencia_desde)} al ${formatFecha(tema.vigencia_hasta)}`
                  : `Desde el ${formatFecha(tema.vigencia_desde)}, sin fecha de fin`}
              </span>
            </div>
            {tema.franjas.length === 0 ? (
              <div className="agenda-meta">
                <span>Sin horario semanal</span>
              </div>
            ) : tema.responsables.length > 1 ? (
              tema.responsables.map((r) => {
                const propias = tema.franjas.filter((f) => f.responsable_id === r.id);
                return (
                  <div key={r.id} className="agenda-meta">
                    <span>
                      <IonIcon icon={timeOutline} /> {r.nombre}: {propias.length ? resumenHorario(propias) : "sin horario"}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="agenda-meta">
                <span>
                  <IonIcon icon={timeOutline} /> {resumenHorario(tema.franjas)}
                </span>
              </div>
            )}

            {gestiona && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                {tema.estado !== "FINALIZADO" && (
                  <IonButton size="small" routerLink={`/agenda/gestion/${temaId}/editar`}>
                    <IonIcon slot="start" icon={createOutline} /> {tema.estado === "BORRADOR" ? "Editar / publicar" : "Editar"}
                  </IonButton>
                )}
                {tema.estado === "PUBLICADO" && (
                  <IonButton size="small" fill="outline" color="warning" onClick={() => cambiarEstado("PAUSADO")}>
                    <IonIcon slot="start" icon={pauseOutline} /> Pausar
                  </IonButton>
                )}
                {tema.estado === "PAUSADO" && (
                  <IonButton size="small" fill="outline" color="success" onClick={() => cambiarEstado("PUBLICADO")}>
                    <IonIcon slot="start" icon={playOutline} /> Reactivar
                  </IonButton>
                )}
                {tema.estado !== "FINALIZADO" && (
                  <IonButton size="small" fill="outline" color="danger" onClick={() => cambiarEstado("FINALIZADO")}>
                    <IonIcon slot="start" icon={stopOutline} /> Finalizar
                  </IonButton>
                )}
              </div>
            )}
          </div>

          {/* ---------------- Indicadores ---------------- */}
          <div className="agenda-stats">
            <div className="agenda-stat">
              <div className="agenda-stat-valor">{st.proximas || 0}</div>
              <div className="agenda-stat-etiqueta">Próximas</div>
            </div>
            <div className="agenda-stat">
              <div className="agenda-stat-valor" style={{ color: st.pendientes ? "#b45309" : undefined }}>{st.pendientes || 0}</div>
              <div className="agenda-stat-etiqueta">Por aprobar</div>
            </div>
            <div className="agenda-stat">
              <div className="agenda-stat-valor">
                {st.presentes || 0}/{(st.presentes || 0) + (st.ausentes || 0)}
              </div>
              <div className="agenda-stat-etiqueta">Asistieron</div>
            </div>
            <div className="agenda-stat">
              <div className="agenda-stat-valor">{st.encuesta_promedio ? Number(st.encuesta_promedio).toFixed(1) : "—"}</div>
              <div className="agenda-stat-etiqueta">Encuesta ({st.encuestas || 0})</div>
            </div>
          </div>

          <div className="agenda-chips">
            <button type="button" className={`agenda-chip${vista === "RESERVAS" ? " activo" : ""}`} onClick={() => setVista("RESERVAS")}>
              Reservas
            </button>
            <button type="button" className={`agenda-chip${vista === "TURNOS" ? " activo" : ""}`} onClick={() => setVista("TURNOS")}>
              Ocupación de turnos
            </button>
          </div>

          {vista === "RESERVAS" ? (
            <div className="agenda-card">
              <div className="agenda-form-fila" style={{ alignItems: "center" }}>
                <TextField size="small" label="Desde" type="date" variant="outlined" value={desde}
                  InputLabelProps={{ shrink: true }} onChange={(e) => e.target.value && setDesde(e.target.value)} />
                <TextField size="small" label="Hasta" type="date" variant="outlined" value={hasta}
                  InputLabelProps={{ shrink: true }} onChange={(e) => e.target.value && setHasta(e.target.value)} />
                <IonButton fill="outline" size="small" onClick={exportar} disabled={!reservas.length} style={{ flex: "0 0 auto" }}>
                  <IonIcon slot="start" icon={downloadOutline} /> Excel
                </IonButton>
                <IonButton fill="outline" size="small" onClick={descargarCalendario} disabled={!reservas.length}
                  style={{ flex: "0 0 auto" }}>
                  <IonIcon slot="start" icon={calendarOutline} /> Calendario
                </IonButton>
              </div>
              <small className="agenda-ayuda">
                "Calendario" descarga las citas confirmadas y pendientes del rango
                {tema.tipo_agenda === "GRUPAL" ? " (una por sesión, con la lista de anotados)" : ""} para importarlas de
                una vez en Google Calendar (Configuración &gt; Importar y exportar), Outlook o el iPhone. Es una foto del
                momento: si después cambian las citas, volvé a descargarlo e importarlo.
              </small>
              <div className="agenda-chips" style={{ marginTop: 12 }}>
                {FILTROS.map((f) => (
                  <button key={f.valor} type="button" className={`agenda-chip${filtro === f.valor ? " activo" : ""}`}
                    onClick={() => setFiltro(f.valor)}>
                    {f.etiqueta}
                  </button>
                ))}
              </div>

              {cargandoReservas ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                  <CircularProgress size={28} />
                </div>
              ) : visibles.length === 0 ? (
                <div className="agenda-vacio">No hay reservas en este rango y filtro.</div>
              ) : (
                agruparPorDia(visibles, (r) => r.fecha_inicio).map(([dia, lista]) => (
                  <div key={dia}>
                    <div className="agenda-dia-titulo">{formatDia(dia)}</div>
                    {lista.map((r) => (
                      <div key={r.id} className="agenda-reserva">
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ fontSize: 14 }}>
                            <strong>{hora(r.fecha_inicio)}</strong> · {r.nombre || `C.I. ${r.cedula}`}
                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                              C.I. {r.cedula}
                              {r.cargo ? ` · ${r.cargo}` : ""}
                              {r.responsable_nombre && tema.responsables.length > 1 ? ` · con ${r.responsable_nombre}` : ""}
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <IonBadge color={ESTADO_RESERVA[r.estado].color}>{ESTADO_RESERVA[r.estado].etiqueta}</IonBadge>
                            {r.asistencia && (
                              <div style={{ fontSize: 12, marginTop: 4, color: r.asistencia === "PRESENTE" ? "#047857" : "#b91c1c" }}>
                                {r.asistencia === "PRESENTE" ? "Asistió" : "No asistió"}
                              </div>
                            )}
                          </div>
                        </div>
                        {r.motivo && (
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            Motivo: {r.motivo}
                          </div>
                        )}
                        {r.respuestas && r.respuestas.length > 0 && (
                          <div className="agenda-respuestas">
                            {r.respuestas.map((a, i) => (
                              <div key={i}>
                                <strong>{a.pregunta}:</strong> {a.valor}
                              </div>
                            ))}
                          </div>
                        )}
                        {r.respuestas === null && (
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            <IonIcon icon={lockClosedOutline} /> Respuestas visibles solo para el responsable
                          </div>
                        )}
                        {r.encuesta_puntaje && (
                          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                            Encuesta: {"★".repeat(r.encuesta_puntaje)}
                            {r.encuesta_comentario ? ` — ${r.encuesta_comentario}` : ""}
                          </div>
                        )}

                        {(r.puede_atender || r.estado === "CONFIRMADA" || r.estado === "PENDIENTE") && (
                          <div className="agenda-reserva-acciones">
                            {(r.estado === "CONFIRMADA" || r.estado === "PENDIENTE") && (
                              <IonButton size="small" fill="clear" href={urlGoogleCalendar(eventoDeReservaGestion(tema, r, reservas))}
                                target="_blank" rel="noopener noreferrer"
                                title={tema.tipo_agenda === "GRUPAL" ? "Agrega la sesión con la lista de anotados" : "Agrega esta cita"}>
                                <IonIcon slot="start" icon={logoGoogle} /> Google Calendar
                              </IonButton>
                            )}
                            {r.puede_atender && r.estado === "PENDIENTE" && (
                              <>
                                <IonButton size="small" color="success" disabled={trabajando === r.id}
                                  onClick={() => ejecutar(r.id, () => AgendaAPI.responder(r.id, true))}>
                                  <IonIcon slot="start" icon={checkmarkOutline} /> Aprobar
                                </IonButton>
                                <IonButton size="small" fill="outline" color="danger" disabled={trabajando === r.id}
                                  onClick={() => pedirMotivo("Rechazar solicitud", "Rechazar",
                                    (m) => ejecutar(r.id, () => AgendaAPI.responder(r.id, false, m)))}>
                                  <IonIcon slot="start" icon={closeOutline} /> Rechazar
                                </IonButton>
                              </>
                            )}
                            {r.puede_atender && r.estado === "CONFIRMADA" && r.empezo && (
                              <>
                                <IonButton size="small" fill={r.asistencia === "PRESENTE" ? "solid" : "outline"} color="success"
                                  disabled={trabajando === r.id} onClick={() => marcarAsistencia(r, "PRESENTE")}>
                                  Asistió
                                </IonButton>
                                <IonButton size="small" fill={r.asistencia === "AUSENTE" ? "solid" : "outline"} color="danger"
                                  disabled={trabajando === r.id} onClick={() => marcarAsistencia(r, "AUSENTE")}>
                                  No asistió
                                </IonButton>
                              </>
                            )}
                            {r.puede_atender && !r.empezo && ["PENDIENTE", "CONFIRMADA", "ESPERA"].includes(r.estado) && (
                              <IonButton size="small" fill="clear" color="medium" disabled={trabajando === r.id}
                                onClick={() => pedirMotivo("Cancelar cita", "Cancelar cita",
                                  (m) => ejecutar(r.id, () => AgendaAPI.cancelarGestion(r.id, m)))}>
                                Cancelar
                              </IonButton>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="agenda-card">
              <div className="agenda-form-fila" style={{ alignItems: "center" }}>
                <TextField size="small" label="Desde" type="date" variant="outlined" value={turnosDesde}
                  InputLabelProps={{ shrink: true }} helperText="Se muestran 14 días"
                  onChange={(e) => e.target.value && setTurnosDesde(e.target.value)} />
              </div>
              {cargandoTurnos ? (
                <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                  <CircularProgress size={28} />
                </div>
              ) : turnos.length === 0 ? (
                <div className="agenda-vacio">No hay turnos en esos días (revisá horarios, vigencia y bloqueos).</div>
              ) : (
                agruparPorDia(turnos, (t) => t.inicio).map(([dia, lista]) => (
                  <div key={dia}>
                    <div className="agenda-dia-titulo">{formatDia(dia)}</div>
                    <div className="agenda-turnos">
                      {lista.map((t) => (
                        <div key={`${t.responsable_id}-${t.inicio}`}
                          className={`agenda-turno${t.disponibles === 0 ? " lleno" : ""}${t.ocupados > 0 && t.disponibles > 0 ? " mio" : ""}`}
                          style={{ cursor: "default" }}>
                          {hora(t.inicio)}
                          <small>
                            {t.ocupados}/{t.cupo}
                            {t.en_espera ? ` · ${t.en_espera} esp.` : ""}
                          </small>
                          {tema.responsables.length > 1 && <small>{nombreResp(t.responsable_id)}</small>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AgendaTemaDetalle;
